import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, Sparkles, Coins, BarChart3, Flame, X } from "lucide-react";
import { useI18n } from "../i18n";
import VoiceCapture from "./VoiceCapture";

const LONG_PRESS_MS = 380;

// Always-visible floating mic button + Quick Bar (long-press menu).
// Tap = open full-screen Siri-style voice capture overlay (records + auto-stops + sends to assistant)
// Long-press = open radial sheet with 4 quick actions
export default function VoiceFab() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const navigate = useNavigate();
  const location = useLocation();
  const [pulse, setPulse] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);

  // Hide only on the standalone /login route — keep available everywhere else (incl. /assistente)
  const hidden = location.pathname === "/login" || location.pathname === "/cadastro";

  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 12000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const startPress = () => {
    longPressed.current = false;
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      // haptic if available
      if (navigator.vibrate) navigator.vibrate(15);
      setMenuOpen(true);
    }, LONG_PRESS_MS);
  };

  const cancelPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleClick = (e) => {
    cancelPress();
    if (longPressed.current) {
      // Long press already triggered the menu — suppress click navigation
      e.preventDefault();
      e.stopPropagation();
      longPressed.current = false;
      return;
    }
    setVoiceOpen(true);
  };

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const openVoice = () => {
    setMenuOpen(false);
    setVoiceOpen(true);
  };

  if (hidden) return null;

  const actions = [
    {
      id: "voice",
      icon: Mic,
      label: pt ? "Falar com assistente" : "Talk to assistant",
      sub: pt ? "Pergunta livre por voz" : "Free voice question",
      onClick: () => openVoice(),
      tone: "gold",
    },
    {
      id: "weekly",
      icon: Flame,
      label: pt ? "Decisão da Semana" : "Decision of the Week",
      sub: pt ? "Curadoria IA semanal" : "Weekly AI pick",
      onClick: () => go("/"),
      tone: "amber",
    },
    {
      id: "compare",
      icon: BarChart3,
      label: pt ? "Comparar 2 ativos" : "Compare 2 assets",
      sub: pt ? "Cruzamento global" : "Global cross-asset",
      onClick: () => go("/comparador"),
      tone: "emerald",
    },
    {
      id: "quote",
      icon: Coins,
      label: pt ? "Cotação rápida" : "Quick quote",
      sub: pt ? "Moedas, cripto, índices" : "FX, crypto, indices",
      onClick: () => go("/mercado"),
      tone: "sky",
    },
  ];

  return (
    <>
      <button
        onClick={handleClick}
        onPointerDown={startPress}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        onPointerCancel={cancelPress}
        onContextMenu={(e) => e.preventDefault()}
        data-testid="voice-fab"
        aria-label={pt ? "Falar com assistente" : "Talk to assistant"}
        className="fixed right-4 bottom-20 sm:right-6 sm:bottom-6 z-[55] group select-none"
        style={{ touchAction: "manipulation" }}
      >
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full bg-gold-500/40 ${pulse ? "animate-ping" : ""}`}
          style={{ animationDuration: "2.4s" }}
        />
        <span className="relative grid place-items-center w-14 h-14 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 text-black shadow-[0_8px_24px_rgba(233,185,52,0.45)] border border-gold-300/60 group-hover:scale-105 group-active:scale-95 transition-transform">
          <Mic size={22} strokeWidth={2.4} />
        </span>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-md bg-black/85 border border-gold-500/30 text-xs font-mono uppercase tracking-[0.18em] text-gold-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          {pt ? "Toque · Segure para menu" : "Tap · Hold for menu"}
        </span>
      </button>

      {menuOpen && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-md animate-in fade-in"
          onClick={() => setMenuOpen(false)}
          data-testid="voice-quickbar-overlay"
        >
          <div
            className="absolute right-3 left-3 sm:right-6 sm:left-auto bottom-24 sm:bottom-24 sm:max-w-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="voice-quickbar"
          >
            <div className="rounded-2xl border border-gold-500/30 bg-black/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.7)] overflow-hidden">
              <div className="px-5 pt-5 pb-3 flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
                    <Sparkles size={10} /> {pt ? "Quick Bar" : "Quick Bar"}
                  </div>
                  <h3 className="font-display text-xl text-white mt-0.5">
                    {pt ? "O que você quer fazer?" : "What do you want to do?"}
                  </h3>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close"
                  className="text-neutral-500 hover:text-white p-1"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-3 pb-3 space-y-1.5">
                {actions.map((a, i) => (
                  <button
                    key={a.id}
                    onClick={a.onClick}
                    data-testid={`quickbar-${a.id}`}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-white/[0.02] hover:bg-gold-500/[0.08] border border-white/5 hover:border-gold-500/30 transition-all rise-in text-left"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${
                      a.tone === "gold"    ? "bg-gold-500/15 border border-gold-500/40 text-gold-300" :
                      a.tone === "amber"   ? "bg-orange-500/15 border border-orange-500/30 text-orange-300" :
                      a.tone === "emerald" ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" :
                                             "bg-sky-500/15 border border-sky-500/30 text-sky-300"
                    }`}>
                      <a.icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-display text-base leading-tight truncate">{a.label}</div>
                      <div className="text-xs text-neutral-500 mt-0.5 truncate">{a.sub}</div>
                    </div>
                    <span className="text-neutral-600 font-mono text-xs">→</span>
                  </button>
                ))}
              </div>
              <div className="px-5 py-2.5 text-center text-[9px] font-mono uppercase tracking-[0.22em] text-neutral-700 border-t border-white/5">
                {pt ? "Toque · Segure 0,4s para abrir este menu" : "Tap · Hold 0.4s to open this menu"}
              </div>
            </div>
          </div>
        </div>
      )}

      <VoiceCapture open={voiceOpen} onClose={() => setVoiceOpen(false)} />
    </>
  );
}
